/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useToast } from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';

import { axios } from '@/services/axios';
import { query, multiSelectDataMapper } from 'utils/utils';

import getService from 'API/Service/getService';

import {
    LoadingIndicator,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@/components/v4';

import FormBase from '@/components/forms/new/FormBase';
import BreadcrumbTrail from 'components/BreadcrumbTrail';

// Form field components
import OperatorConnectCallingProfilesField from '../../serviceprofiles/edit/formFields/OperatorConnectCallingProfilesField';
import OperatorConnectRegionsField from '../../serviceprofiles/edit/formFields/OperatorConnectRegionsField';
import OperatorConnectVoicePoliciesField from '../../serviceprofiles/edit/formFields/OperatorConnectVoicePoliciesField';
import OperatorConnectDialPlansField from '../../serviceprofiles/edit/formFields/OperatorConnectDialPlansField';
import OperatorConnectAuthPolicyField from '../../serviceprofiles/edit/formFields/OperatorConnectAuthPolicyField';

const authPolicyOptions = [
    { label: 'Disabled', value: 0 },
    { label: 'Enabled', value: 1 },
    { label: 'Required', value: 2 },
];

export function OC() {
    const navigate = useNavigate();
    const { id: serviceId } = useParams();
    const toast = useToast();

    // State for available options
    const [availableOperators, setAvailableOperators] = useState([]);
    const [availableCallingProfiles, setAvailableCallingProfiles] = useState(
        [],
    );
    const [availableRegions, setAvailableRegions] = useState([]);
    const [availableVoicePolicies, setAvailableVoicePolicies] = useState([]);
    const [availableDialPlans, setAvailableDialPlans] = useState([]);
    const [selectedServiceData, setSelectedServiceData] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const { darkMode } = useSelector((state) => state.settings);
    const companyID = useSelector(
        (state) => state.navigationLists.currentCompany,
    );

    // Handle form submission
    const handleSubmit = async (values, actions) => {
        setIsLoading(true);
        setError(null);

        try {
            const params = {
                id: selectedServiceData.id,
                name: values.name,
                companyID: companyID,
                authPolicy: values.authPolicy,
                operatorProfileID: values.operators?.[0]?.value || null,
                dialPlans:
                    values.dialPlans?.map((dp) => ({
                        id: dp.value,
                        name: dp.label,
                    })) || [],
                voicePolicies:
                    values.voicePolicies?.map((vp) => ({
                        id: vp.value,
                        name: vp.label,
                    })) || [],
                regions:
                    values.regions?.map((re) => ({
                        id: re.value,
                        name: re.label,
                    })) || [],
                callingProfiles:
                    values.callingProfiles?.map((cp) => ({
                        id: cp.value,
                        name: cp.label,
                    })) || [],
            };

            await axios.put('/Service/OperatorConnect', params);

            toast({
                title: 'Service updated successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
                description: 'Operator Connect service has been updated!',
            });
            navigate('/dashboard-services');
        } catch (error) {
            setError(
                error?.response?.data?.message ||
                    'An error occurred while updating the service.',
            );
            actions.setSubmitting(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackButton = () => {
        navigate('/dashboard-services');
    };

    const { isLoading: serviceDataLoading } = useQuery({
        queryKey: ['ocEditServiceData', companyID, serviceId],
        queryFn: async () => {
            // Load existing service data
            const serviceRes = await getService(serviceId);
            const serviceData = serviceRes.data;

            // Load available options
            const [opRes, dpRes] = await Promise.all([
                axios.get(
                    `/Services/OperatorConnect/Company/${companyID}/OperatorProfiles/Available`,
                ),
                axios.get(
                    `/Services/OperatorConnect/Company/${companyID}/DialPlans/Available`,
                ),
            ]);

            const operators = opRes.data.map(multiSelectDataMapper);

            // Merge available dial plans with current service dial plans
            const availableDialPlansData = dpRes.data.map(
                multiSelectDataMapper,
            );
            const currentDialPlans =
                serviceRes.data.dialPlans?.map(multiSelectDataMapper) || [];
            const mergedDialPlans = [...availableDialPlansData];
            currentDialPlans.forEach((currentDP) => {
                if (
                    !mergedDialPlans.find(
                        (available) => available.value === currentDP.value,
                    )
                ) {
                    mergedDialPlans.push(currentDP);
                }
            });

            let mergedCallingProfiles = [];
            let mergedRegions = [];

            // Load dependent data if operator is selected
            if (serviceData.operatorProfile?.id) {
                const cpRes = await axios.get(
                    `/Services/OperatorConnect/Company/${companyID}/Profile/${serviceData.operatorProfile.id}/CallingProfiles/Available`,
                );
                const availableCallingProfilesData = cpRes.data.map(
                    multiSelectDataMapper,
                );
                const currentCallingProfiles =
                    serviceData.callingProfiles?.map(multiSelectDataMapper) ||
                    [];
                mergedCallingProfiles = [...availableCallingProfilesData];
                currentCallingProfiles.forEach((currentCP) => {
                    if (
                        !mergedCallingProfiles.find(
                            (available) => available.value === currentCP.value,
                        )
                    ) {
                        mergedCallingProfiles.push(currentCP);
                    }
                });

                // Load regions if calling profiles exist
                if (serviceData.callingProfiles?.length > 0) {
                    const regRes = await axios.get(
                        `/Services/OperatorConnect/Company/${companyID}/Profile/${serviceData.operatorProfile.id}/Regions/Available?CallingProfileIDs=${query(serviceData.callingProfiles)}`,
                    );
                    const availableRegionsData = regRes.data.map(
                        multiSelectDataMapper,
                    );
                    const currentRegions =
                        serviceData.regions?.map(multiSelectDataMapper) || [];
                    mergedRegions = [...availableRegionsData];
                    currentRegions.forEach((currentReg) => {
                        if (
                            !mergedRegions.find(
                                (available) =>
                                    available.value === currentReg.value,
                            )
                        ) {
                            mergedRegions.push(currentReg);
                        }
                    });
                }
            }

            let mergedVoicePolicies = [];
            if (
                serviceData.operatorProfile?.id &&
                serviceData.callingProfiles?.length > 0 &&
                serviceData.regions?.length > 0
            ) {
                const vpRes = await axios.get(
                    `/Services/OperatorConnect/Company/${companyID}/Profile/${serviceData.operatorProfile.id}/VoicePolicies/Available?CallingProfileIDs=${query(serviceData.callingProfiles)}&RegionIDs=${query(serviceData.regions)}`,
                );
                const availableVoicePoliciesData = vpRes.data.map(
                    multiSelectDataMapper,
                );
                const currentVoicePolicies =
                    serviceData.voicePolicies?.map(multiSelectDataMapper) || [];
                mergedVoicePolicies = [...availableVoicePoliciesData];
                currentVoicePolicies.forEach((currentVP) => {
                    if (
                        !mergedVoicePolicies.find(
                            (available) => available.value === currentVP.value,
                        )
                    ) {
                        mergedVoicePolicies.push(currentVP);
                    }
                });
            }

            return {
                serviceData,
                operators,
                dialPlans: mergedDialPlans,
                callingProfiles: mergedCallingProfiles,
                regions: mergedRegions,
                voicePolicies: mergedVoicePolicies,
            };
        },
        enabled: Boolean(companyID && serviceId),
        onSuccess: ({
            serviceData,
            operators,
            dialPlans,
            callingProfiles,
            regions,
            voicePolicies,
        }) => {
            setSelectedServiceData(serviceData);
            setAvailableOperators(operators);
            setAvailableDialPlans(dialPlans);
            setAvailableCallingProfiles(callingProfiles);
            setAvailableRegions(regions);
            setAvailableVoicePolicies(voicePolicies);
        },
        onError: (err) => {
            setError(
                err?.response?.data?.message || 'Failed to load service data',
            );
        },
    });

    if (!serviceId) {
        return <Navigate to="/dashboard-services" />;
    }

    if (serviceDataLoading || !selectedServiceData) {
        return <LoadingIndicator />;
    }

    // Map values to options for form initialization
    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) =>
                        options.find((o) => o.value === (v.value ?? v.id ?? v)),
                    )
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find(
                (o) => o.value === (values.value ?? values.id ?? values),
            ) || null
        );
    };

    const initialValues = {
        name: selectedServiceData?.name || '',
        // Not rendered in the UI (#2592) but kept in form state so it is still
        // submitted as operatorProfileID on save.
        operators: selectedServiceData?.operatorProfile
            ? [
                  {
                      id: selectedServiceData.operatorProfile.id,
                      name: selectedServiceData.operatorProfile.name,
                      value: selectedServiceData.operatorProfile.id,
                      label: selectedServiceData.operatorProfile.name,
                  },
              ]
            : [],
        callingProfiles: mapToOptions(
            selectedServiceData?.callingProfiles,
            availableCallingProfiles,
        ),
        regions: mapToOptions(selectedServiceData?.regions, availableRegions),
        voicePolicies: mapToOptions(
            selectedServiceData?.voicePolicies,
            availableVoicePolicies,
        ),
        dialPlans: mapToOptions(
            selectedServiceData?.dialPlans,
            availableDialPlans,
        ),
        authPolicy: selectedServiceData?.authPolicy || 0,
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['/dashboard-services', { index: 0 }] },
        { label: 'Edit Operator Connect Service', path: '' },
    ];

    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`Edit ${selectedServiceData?.name || 'Operator Connect Service'}`}
                subTitleHeader={true}
                initialValues={initialValues}
                finalFormSubmit={handleSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {({ values, setFieldValue, errors, touched }) => [
                    // Operator is intentionally not rendered here (#2592) — it is
                    // still submitted via the `operators` form value (populated
                    // from the loaded service data) but is not exposed in the UI.
                    <OperatorConnectCallingProfilesField
                        key="callingProfiles"
                        value={values.callingProfiles}
                        onChange={(value) => {
                            setFieldValue('callingProfiles', value);
                        }}
                        options={availableCallingProfiles}
                        darkMode={darkMode}
                        error={errors.callingProfiles}
                        showError={touched.callingProfiles}
                    />,
                    <OperatorConnectRegionsField
                        key="regions"
                        value={values.regions}
                        onChange={(value) => {
                            setFieldValue('regions', value);
                        }}
                        options={availableRegions}
                        darkMode={darkMode}
                        error={errors.regions}
                        showError={touched.regions}
                    />,
                    <OperatorConnectVoicePoliciesField
                        key="voicePolicies"
                        value={values.voicePolicies}
                        onChange={(value) => {
                            setFieldValue('voicePolicies', value);
                        }}
                        options={availableVoicePolicies}
                        darkMode={darkMode}
                        error={errors.voicePolicies}
                        showError={touched.voicePolicies}
                    />,
                    <OperatorConnectDialPlansField
                        key="dialPlans"
                        value={values.dialPlans}
                        onChange={(value) => {
                            setFieldValue('dialPlans', value);
                        }}
                        options={availableDialPlans}
                        darkMode={darkMode}
                        error={errors.dialPlans}
                        showError={touched.dialPlans}
                    />,
                    <OperatorConnectAuthPolicyField
                        key="authPolicy"
                        field={{ name: 'authPolicy', value: values.authPolicy }}
                        form={{ values, setFieldValue, errors, touched }}
                        options={authPolicyOptions}
                        darkMode={darkMode}
                    />,
                    error && (
                        <Alert
                            key="error"
                            status="error"
                            sx={{ marginTop: '1rem' }}>
                            <AlertIcon />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ),
                ]}
            </FormBase>
        </>
    );
}
